import { useEffect, useState } from "react";
// API
import {
  deleteHubNote,
  getHubNotes,
  postHubNotes,
} from "@intelligentlilli/api-layer";
// State
import { useDispatch } from "react-redux";
import { setLoading } from "../../State/slices/session-slice";
import { parseISO, isSameDay } from "date-fns";
import { useAmplitudeTracking } from "./useAmplitudeTracking";

export const useHubDailyNotes = (navigate, server, id) => {
  // Local state
  // Bool to handle refreshing hub notes (via useEffect hook) when required
  const [hubNotes, setHubNotes] = useState();
  const [refreshHubNotes, setRefreshHubNotes] = useState(true);
  const [error, setError] = useState();
  // Redux state
  const dispatch = useDispatch();

  const track = useAmplitudeTracking();

  useEffect(() => {
    if (refreshHubNotes) {
      setRefreshHubNotes(false);
      getHubNotes(server, id, "web")
        .then((res) => {
          if (res.ok) {
            setHubNotes(res.data);
          } else {
            setError("Error getting service user notes");
          }
          dispatch(setLoading(false));
        })
        .catch((err) => {
          setError("Error getting service user notes");
          console.log("This is the error", err);
          dispatch(setLoading(false));
        });
    }
  }, [refreshHubNotes, server, id, navigate, dispatch]);

  const getHubNoteForTheDay = (
    date // Look through the service user notes and find the one for the day selected
  ) =>
    hubNotes?.find((hubNote) => isSameDay(parseISO(hubNote.ts), date))?.value;

  const deleteHubNoteForTheDay = (date) => {
    deleteHubNote(
      server,
      id,
      hubNotes?.find((hubNote) => isSameDay(parseISO(hubNote.ts), date))?.id,
      "web"
    )
      .then((res) => {
        if (!res.ok) {
          setError("Error deleting note");
          if (res.status === 401) {
            navigate("/login");
          } else {
            track("Service User Notes", { action: "note_deleted" });
          }
        }
      })
      .catch((_) => {
        setError("Error deleting note");
      });
  };

  const deleteNote = (noteId) => {
    dispatch(setLoading(true));
    deleteHubNote(server, id, noteId, "web")
      .then((res) => {
        if (!res.ok) {
          setError("Error deleting note");
          if (res.status === 401) {
            navigate("/login");
          }
        } else {
          track("Service User Notes", { action: "note_deleted" });
        }

        setRefreshHubNotes(true);
      })
      .catch((_) => {
        setError("Error deleting note");
        dispatch(setLoading(false));
      });
  };

  const postHubNoteForTheDay = (note, date, callback) => {
    postHubNotes(server, id, note, Date.parse(date), "web")
      .then((res) => {
        if (!res.ok) {
          setError("Error saving note");
        } else {
          track("Service User Notes", { action: "form_submitted" });
          setRefreshHubNotes(true);
          callback && callback();
        }
      })
      .catch((_) => {
        setError("Error saving note");
      })
      .finally((_) => {
        dispatch(setLoading(false));
      });
  };

  const onSaveNote = (note, date, callback) => {
    const newNote = note?.length > 0; // I.e. if the new note is not an empty string
    const oldNoteExists = getHubNoteForTheDay(date); // If a note already exists for the day

    dispatch(setLoading(true));

    if (oldNoteExists) {
      deleteHubNoteForTheDay(date);
    }
    if (newNote) {
      postHubNoteForTheDay(note, date, callback);
    } else {
      setRefreshHubNotes(true);
    }
  };

  return {
    onSaveNote,
    getHubNoteForTheDay,
    setRefreshHubNotes,
    hubNotes,
    error,
    deleteNote,
  };
};
